
import { supabase } from "@/integrations/supabase/client";
import { siteMetadata } from "@/config/siteMetadata";

export interface EnhancedSeoMetadata {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  articleAuthor?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  schema?: any;
}

// Comprehensive keyword mapping for all loan product pages
const loanProductKeywords = {
  asuntolaina: [
    "asuntolaina", "kilpailuta asuntolaina", "asuntolainan korko", "asuntolainatarjous", 
    "asunnon osto", "asuntolainan hinta", "edullinen asuntolaina", "asuntolainan vertailu", 
    "kiinteistölaina", "asuntolainan hakeminen", "ensimmäinen asuntolaina", "asuntolainan marginaali", 
    "asuntolainan kulut", "asuntolainan pankeista", "asuntolainan ehdot", "asuntolainasimulaattori"
  ],
  kulutusluotto: [
    "kulutusluotto", "kilpailuta kulutusluotto", "kulutusluoton korko", "vakuudeton laina", 
    "henkilökohtainen laina", "kulutusluottotarjous", "edullinen kulutusluotto", "kulutusluoton vertailu",
    "pikalaina", "kulutusluoton hakeminen", "joustavat maksuehdot", "nopea lainapäätös", 
    "kulutusluoton ehdot", "lyhytaikainen laina"
  ],
  autolaina: [
    "autolaina", "kilpailuta autolaina", "auton rahoitus", "autolainan korko", "uuden auton laina", 
    "käytetyn auton laina", "autokaupan rahoitus", "edullinen autolaina", "ajoneuvon rahoitus", 
    "autolainan kilpailutus", "autolaina vertailu", "auton osto lainalla", "ajoneuvon vakuuslaina", 
    "autolainan ehdot", "auton rahoitusyhtiö"
  ],
  yhdistelylaina: [
    "yhdistelylaina", "kilpailuta yhdistelylaina", "yhdistä lainat", "lainojen yhdistäminen", 
    "velkojen yhdistäminen", "edullisempi laina", "lainasaneeraus", "velkojen järjestely",
    "velka-apu", "edullinen yhdistelylaina", "yhdistelylainan kilpailutus", "lainojen järjestely", 
    "maksuhäiriömerkintä", "velkojen uudelleenjärjestely", "yhdistelylainan ehdot", "velkataakka"
  ],
  venelaina: [
    "venelaina", "kilpailuta venelaina", "veneen rahoitus", "venelainan korko", "uuden veneen laina", 
    "käytetyn veneen laina", "venekaupan rahoitus", "edullinen venelaina", "veneluotto",
    "venelainan kilpailutus", "veneen osamaksu", "venerahoitus", "venelainan ehdot"
  ],
  moottoripyoralaina: [
    "moottoripyörälaina", "moottoripyörän rahoitus", "mp-laina", "edullinen moottoripyörälaina",
    "moottoripyörälainan kilpailutus", "moottoripyörän osamaksu", "mopo rahoitus"
  ],
  remonttilaina: [
    "remonttilaina", "kilpailuta remonttilaina", "remontin rahoitus", "kodin kunnostus", 
    "sisustusremontti", "keittiöremontti", "kylpyhuoneremontti", "edullinen remonttilaina",
    "korjauslaina", "remonttilainan kilpailutus", "remontti luotto", "asunnon remontti laina"
  ],
  mokkilaina: [
    "mökkilaina", "mökin rahoitus", "loma-asunnon laina", "edullinen mökkilaina",
    "mökkilainan kilpailutus", "kesämökin laina", "mökkien rahoitus"
  ],
  joustoluotto: [
    "joustoluotto", "kilpailuta joustoluotto", "joustava rahoitus", "luottolimiitti", 
    "käyttöluotto", "limiitti", "edullinen joustoluotto", "revolving credit",
    "joustavat luotot", "joustoluoton kilpailutus", "limiittilaina", "joustava luotto"
  ],
  lomalaina: [
    "lomalaina", "kilpailuta lomalaina", "loman rahoitus", "matkalaina", "lomarahoitus", 
    "matkan rahoitus", "edullinen lomalaina", "lomalle lainalla", "matkustusluotto",
    "lomalainan kilpailutus", "loma-rahoitus"
  ],
  haalaina: [
    "häälaina", "häiden rahoitus", "hääluotto", "edullinen häälaina",
    "häälainan kilpailutus", "häiden kulut", "hääjuhlan rahoitus"
  ],
  opintolaina: [
    "opintolaina", "opiskelun rahoitus", "opintoluotto", "edullinen opintolaina",
    "opintolainan kilpailutus", "opiskelijalaina", "koulutuksen rahoitus"
  ],
  caravanrahoitus: [
    "caravanrahoitus", "caravan laina", "asuntovaunun rahoitus", "matkailuvaunun laina",
    "caravan osamaksu", "asuntovaunun osamaksu", "matkailuauton rahoitus"
  ],
  ulosottolaskuri: [
    "ulosottolaskuri", "ulosotto", "ulosottovaroja", "ulosoton laskeminen", 
    "velkaneuvonta", "taloudellinen tilanne", "ulosoton määrä", "ulosotto laskuri", 
    "laske ulosotto", "ulosotto palkasta", "ulosotto eläkkeestä", "velallisen oikeudet"
  ],
  pikalaina: [
    "pikalaina", "kilpailuta pikalaina", "nopea laina", "pikarahoitus", "äkilliset menot", 
    "pikavippi", "lyhytaikainen laina", "edullinen pikalaina", "pienlaina", "joustava rahoitus",
    "rahoitusmuoto", "pikalainan kilpailutus", "nopea päätös"
  ],
  yrityslaina: [
    "yrityslaina", "kilpailuta yrityslaina", "yritysrahoitus", "liiketoiminnan rahoitus", 
    "yrityksen kasvu", "yrittäjälaina", "edullinen yrityslaina", "pk-yrityksen rahoitus",
    "yrityksen rahoitus", "yrityslainan kilpailutus", "käyttöpääomalaina", "investointilaina", "yritysluotto"
  ],
  pienkonelaina: [
    "pienkonelaina", "kilpailuta pienkonelaina", "pienkone rahoitus", "työkalujen rahoitus", 
    "puutarhakoneet", "metsäkoneet", "edullinen pienkonelaina", "puutarhakoneiden rahoitus", 
    "moottorisahan rahoitus", "ruohonleikkuri laina", "lumilinkous rahoitus", "työkoneiden rahoitus",
    "aggregaatti laina", "pienkoneiden osamaksu", "pienkone luotto"
  ],
  pienlainatluottokortit: [
    "pienlainat", "luottokortit", "pienlainan vertailu", "luottokortin kilpailutus",
    "edullinen pienlaina", "paras luottokortti", "luottokortin ehdot", "pienlainan korko"
  ]
};

// Generate comprehensive SEO data for loan product pages
export const generateLoanProductSeo = (
  loanType: keyof typeof loanProductKeywords,
  customTitle?: string,
  customDescription?: string
): EnhancedSeoMetadata => {
  const keywords = loanProductKeywords[loanType] || [];
  
  const defaultTitles = {
    asuntolaina: "Asuntolaina kilpailutus | Edullinen asuntolaina | Lainasampo.fi",
    kulutusluotto: "Kulutusluotto kilpailutus | Edullinen kulutusluotto | Lainasampo.fi", 
    autolaina: "Autolaina kilpailutus | Edullinen auton rahoitus | Lainasampo.fi",
    yhdistelylaina: "Yhdistelylaina | Lainojen yhdistäminen | Lainasampo.fi",
    venelaina: "Venelaina kilpailutus | Edullinen veneen rahoitus | Lainasampo.fi",
    moottoripyoralaina: "Moottoripyörälaina | Edullinen MP-rahoitus | Lainasampo.fi",
    remonttilaina: "Remonttilaina | Edullinen remontin rahoitus | Lainasampo.fi",
    mokkilaina: "Mökkilaina | Edullinen mökin rahoitus | Lainasampo.fi",
    joustoluotto: "Joustoluotto | Edullinen luottolimiitti | Lainasampo.fi",
    lomalaina: "Lomalaina | Edullinen loman rahoitus | Lainasampo.fi",
    haalaina: "Häälaina | Edullinen häiden rahoitus | Lainasampo.fi",
    opintolaina: "Opintolaina | Edullinen opiskelun rahoitus | Lainasampo.fi",
    caravanrahoitus: "Caravanrahoitus | Edullinen asuntovaunun rahoitus | Lainasampo.fi",
    ulosottolaskuri: "Ulosottolaskuri 2025 | Laske ulosoton määrä | Lainasampo.fi",
    pikalaina: "Pikalaina | Nopea rahoitus yllättäviin menoihin | Lainasampo.fi",
    yrityslaina: "Yrityslaina | Edullinen yrityksen rahoitus | Lainasampo.fi",
    pienkonelaina: "Pienkonelaina | Puutarhakoneiden rahoitus | Lainasampo.fi",
    pienlainatluottokortit: "Pienlainat ja luottokortit | Vertaile ja kilpailuta | Lainasampo.fi"
  };
  
  const defaultDescriptions = {
    asuntolaina: "Kilpailuta asuntolainasi ja säästä tuhansia euroja. Vertaa yli 20 pankin asuntolainatarjouksia ja löydä edullisimmat ehdot unelmakotisi ostoon.",
    kulutusluotto: "Kilpailuta kulutusluottosi ja löydä edullisimmat ehdot. Vertaa kulutusluottoja useasta pankista ja rahoituslaitoksesta helposti.",
    autolaina: "Kilpailuta autolainasi ja säästä auton ostossa. Vertaa auton rahoitustarjouksia useasta pankista ja löydä edullisimmat ehdot.",
    yhdistelylaina: "Yhdistä lainasi edullisesti ja helpota maksurasitustasi. Kilpailuta yhdistelylaina useasta pankista ja järjestele velkasi.",
    venelaina: "Kilpailuta venelainasi ja rahoita unelmiesi vene edullisesti. Vertaa venelainatarjouksia useasta pankista ja rahoituslaitoksesta.",
    moottoripyoralaina: "Kilpailuta moottoripyörälainasi ja rahoita moottoripyöräsi edullisesti. Vertaa MP-lainatarjouksia useasta pankista.",
    remonttilaina: "Kilpailuta remonttilainasi ja rahoita kotisi kunnostus edullisesti. Vertaa remonttilainatarjouksia useasta pankista.",
    mokkilaina: "Kilpailuta mökkilainasi ja rahoita unelmiesi mökki edullisesti. Vertaa mökkilainatarjouksia useasta pankista.",
    joustoluotto: "Kilpailuta joustoluottosi ja hanki joustava luottolimiitti edullisesti. Vertaa joustoluottoja useasta pankista.",
    lomalaina: "Kilpailuta lomalainasi ja rahoita unelmalomasi edullisesti. Vertaa lomalainatarjouksia useasta pankista ja rahoituslaitoksesta.",
    haalaina: "Kilpailuta häälainasi ja rahoita unelmahääsi edullisesti. Vertaa häälainatarjouksia useasta pankista ja rahoituslaitoksesta.",
    opintolaina: "Kilpailuta opintolainasi ja rahoita opintoisi edullisesti. Vertaa opintolainatarjouksia useasta pankista ja rahoituslaitoksesta.",
    caravanrahoitus: "Kilpailuta caravanrahoituksesi ja rahoita asuntovaunusi edullisesti. Vertaa caravan-rahoitustarjouksia useasta pankista.",
    ulosottolaskuri: "Ilmainen ulosottolaskuri 2025 - Laske ulosoton määrä palkasta tai eläkkeestä. Tietoa ulosotosta ja velallisen oikeuksista.",
    pikalaina: "Kilpailuta pikalainasi ja löydä nopea rahoitus yllättäviin menoihin. Vertaa pikalainatarjouksia useasta rahoituslaitoksesta.",
    yrityslaina: "Kilpailuta yrityslainasi ja löydä edullinen rahoitus yrityksen tarpeisiin. Vertaa yrityslainatarjouksia useasta pankista.",
    pienkonelaina: "Kilpailuta pienkonelainasi ja rahoita puutarhakoneet edullisesti. Vertaa pienkonelainatarjouksia useasta rahoituslaitoksesta.",
    pienlainatluottokortit: "Vertaile pienlainoja ja luottokortteja. Kilpailuta ja löydä edullisimmat pienlainat ja parhaat luottokortit yhdessä paikassa."
  };
  
  return {
    title: customTitle || defaultTitles[loanType],
    description: customDescription || defaultDescriptions[loanType],
    keywords,
    ogTitle: customTitle || defaultTitles[loanType],
    ogDescription: customDescription || defaultDescriptions[loanType],
    ogImage: "/lainasampo-social-card.jpg",
    schema: generateLoanProductSchema(loanType, { description: defaultDescriptions[loanType] })
  };
};

// Generate schema markup for loan products
export const generateLoanProductSchema = (loanType: string, pageData: any) => {
  return {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    "name": `${loanType} - Lainasampo`,
    "description": pageData.description,
    "provider": {
      "@type": "FinancialService",
      "name": "Lainasampo Oy",
      "url": "https://lainasampo.fi",
      "telephone": "+358 9 123 4567",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Mannerheimintie 20 B",
        "addressLocality": "Helsinki", 
        "postalCode": "00100",
        "addressCountry": "FI"
      }
    },
    "category": "Loans",
    "audience": {
      "@type": "Audience",
      "geographicArea": {
        "@type": "Country",
        "name": "Finland"
      }
    }
  };
};

// Enhanced route-to-SEO mapping with all pages
export const getEnhancedSeoDataForRoute = (route: string): EnhancedSeoMetadata => {
  const routeSeoMap: Record<string, EnhancedSeoMetadata> = {
    "/": {
      title: "Lainasampo.fi - Kilpailuta lainasi ja säästä rahaa",
      description: "Kilpailutamme lainasi yli 20 pankissa ja rahoitusyhtiössä. Hae edullinen laina ilmaiseksi. Vertaile kulutusluottoja, asuntolainoja ja autolainoja helposti.",
      keywords: ["lainasampo", "kilpailuta laina", "lainavertailu", "edullinen laina", "kulutusluotto", "asuntolaina", "autolaina", "lainatarjous", "lainojen vertailu", "rahoitus"],
      schema: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Lainasampo",
        "url": "https://lainasampo.fi",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://lainasampo.fi/kilpailuta?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }
    },
    "/kilpailuta": {
      title: "Kilpailuta lainasi ilmaiseksi | Lainasampo.fi",
      description: "Täytä hakemuksesi ilmaiseksi ja saat useita lainatarjouksia vertailtavaksi. Säästä rahaa ja aikaa lainan kilpailutuksella.",
      keywords: ["lainojen kilpailutus", "ilmainen kilpailutus", "lainatarjoukset", "vertaa lainoja", "säästä lainassa", "kilpailuta", "lainahakemus", "rahoitushakemus", "edullinen laina"],
    },
    "/lainatuotteet": {
      title: "Lainatuotteet | Lainasampo.fi - Kaikki lainat yhdessä paikassa",
      description: "Tutustu kaikkiin Lainasampo.fi:n tarjoamiin lainatuotteisiin. Asuntolainat, kulutusluotot, autolainat, yhdistelylainat ja muut lainatuotteet.",
      keywords: ["lainatuotteet", "eri lainatyypit", "asuntolaina", "kulutusluotto", "autolaina", "yhdistelylaina", "venelaina", "remonttilaina", "lainavaihtoehdot", "rahoitustuotteet"],
      schema: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Lainatuotteet",
        "description": "Kaikki lainatuotteet yhdessä paikassa",
        "url": "https://lainasampo.fi/lainatuotteet"
      }
    },
    "/artikkelit": {
      title: "Laina-artikkelit ja asiantuntija-oppaat 2025 - Lainasampo.fi",
      description: "Lue asiantuntijoidemme kirjoittamat oppaat lainoista, säästämisestä ja talouden hallinnasta. Ajankohtaista tietoa ja käytännön vinkkejä rahoituksesta.",
      keywords: ["laina-artikkelit", "lainaoppaat", "asuntolaina opas", "kulutusluotto", "lainanotto", "rahoitus", "talouden hallinta", "säästäminen", "korko", "laina-asiat", "rahoitusneuvonta", "2025 lainaopas"],
      schema: {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Laina-artikkelit ja oppaat",
        "description": "Kattava kokoelma asiantuntija-artikkeleita lainoista ja rahoituksesta",
        "url": "https://lainasampo.fi/artikkelit",
        "publisher": {
          "@type": "Organization",
          "name": "Lainasampo.fi",
          "url": "https://lainasampo.fi",
          "logo": {
            "@type": "ImageObject",
            "url": "https://lainasampo.fi/lainasampo-social-card.jpg"
          }
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://lainasampo.fi/artikkelit?search={search_term}",
          "query-input": "required name=search_term"
        }
      }
    },
    "/meista": {
      title: "Meistä | Lainasampo.fi - Suomen johtava lainavertailupalvelu",
      description: "Tutustu Lainasampo.fi:hin - Suomen luotettuun lainavertailupalveluun. Autamme sinua löytämään edullisimman lainan helposti ja nopeasti.",
      keywords: ["lainasampo", "meistä", "yritys", "lainojen kilpailutus", "asiantuntija", "rahoituspalvelut", "suomalainen yritys", "luotettava kumppani"],
      schema: {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "Meistä - Lainasampo",
        "description": "Tietoa Lainasampo.fi:stä"
      }
    },
    "/yhteydenotto": {
      title: "Ota yhteyttä | Lainasampo.fi",
      description: "Ota yhteyttä Lainasampo.fi:n asiakaspalveluun. Vastaamme mielellämme kaikkiin lainoihin liittyviin kysymyksiin.",
      keywords: ["yhteystiedot", "ota yhteyttä", "asiakaspalvelu", "lainaneuvonta", "apua laina-asioissa", "kontakti", "lainasampo yhteystiedot"],
      schema: {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Yhteystiedot - Lainasampo",
        "description": "Ota yhteyttä Lainasampo.fi:hin"
      }
    },
    "/ukk": {
      title: "UKK - Usein kysytyt kysymykset | Lainasampo.fi",
      description: "Löydä vastaukset yleisimpiin kysymyksiin lainoista ja Lainasampo.fi:n palveluista. Kattava ohje lainojen kilpailuttamiseen.",
      keywords: ["ukk", "usein kysytyt kysymykset", "laina-apua", "lainaneuvonta", "vastaukset", "ohjeita lainoista", "lainojen kilpailutus ohje"],
    },
    "/sv": {
      title: "Lainasampo.fi | Jämför lån och spara pengar",
      description: "Lainasampo.fi hjälper dig att hitta det mest prisvärda lånet. Jämför bolån, konsumentkrediter eller konsolideringslån enkelt med en ansökan.",
      keywords: ["lånematching", "lånförfrågan", "låneförmedling", "lån", "bolån", "konsumentkredit", "finland"],
      schema: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Lainasampo - Låneförmedling i Finland",
        "description": "Jämför lån och hitta bästa lånet i Finland",
        "url": "https://lainasampo.fi/sv"
      }
    },
    // Loan product pages
    "/lainat/asuntolaina": generateLoanProductSeo("asuntolaina"),
    "/lainat/kulutusluotto": generateLoanProductSeo("kulutusluotto"),
    "/lainat/autolaina": generateLoanProductSeo("autolaina"),
    "/lainat/yhdistelylaina": generateLoanProductSeo("yhdistelylaina"),
    "/lainat/venelaina": generateLoanProductSeo("venelaina"),
    "/lainat/moottoripyoralaina": generateLoanProductSeo("moottoripyoralaina"),
    "/lainat/remonttilaina": generateLoanProductSeo("remonttilaina"),
    "/lainat/mokkilaina": generateLoanProductSeo("mokkilaina"),
    "/lainat/joustoluotto": generateLoanProductSeo("joustoluotto"),
    "/lainat/lomalaina": generateLoanProductSeo("lomalaina"),
    "/lainat/haalaina": generateLoanProductSeo("haalaina"),
    "/lainat/opintolaina": generateLoanProductSeo("opintolaina"),
    "/lainat/caravanrahoitus": generateLoanProductSeo("caravanrahoitus"),
    "/lainat/ulosottolaskuri": generateLoanProductSeo("ulosottolaskuri"),
    "/lainat/pikalaina": generateLoanProductSeo("pikalaina"),
    "/lainat/yrityslaina": generateLoanProductSeo("yrityslaina"),
    "/lainat/pienkonelaina": generateLoanProductSeo("pienkonelaina"),
    "/lainat/pienlainat-luottokortit": generateLoanProductSeo("pienlainatluottokortit")
  };
  
  return routeSeoMap[route] || {
    title: siteMetadata.defaultTitle,
    description: siteMetadata.defaultDescription,
    keywords: siteMetadata.defaultKeywords
  };
};

// Save comprehensive SEO metadata to database
export const saveEnhancedSeoMetadata = async (
  pagePath: string, 
  metadata: EnhancedSeoMetadata
): Promise<boolean> => {
  try {
    const { data: session } = await supabase.auth.getSession();
    
    if (!session?.session) {
      console.warn('User not authenticated. Cannot save SEO metadata.');
      return false;
    }
    
    const { error } = await supabase
      .from('seo_keywords')
      .upsert({
        page_path: pagePath,
        title: metadata.title,
        description: metadata.description,
        keywords: metadata.keywords,
        og_title: metadata.ogTitle,
        og_description: metadata.ogDescription,
        og_image: metadata.ogImage,
        schema_markup: metadata.schema,
        last_updated: new Date().toISOString()
      }, { onConflict: 'page_path' });
    
    if (error) {
      console.error('Error saving enhanced SEO metadata:', error);
      return false;
    }
    
    console.info(`Enhanced SEO metadata saved for: ${pagePath}`);
    return true;
  } catch (error) {
    console.error('Error in saveEnhancedSeoMetadata:', error);
    return false;
  }
};

// Bulk update SEO data for all loan product pages
export const updateAllLoanProductSeo = async (): Promise<void> => {
  const allRoutes = [
    '/',
    '/kilpailuta',
    '/lainatuotteet',
    '/artikkelit',
    '/meista',
    '/yhteydenotto',
    '/ukk',
    '/lainat/asuntolaina',
    '/lainat/kulutusluotto', 
    '/lainat/autolaina',
    '/lainat/yhdistelylaina',
    '/lainat/venelaina',
    '/lainat/moottoripyoralaina',
    '/lainat/remonttilaina',
    '/lainat/mokkilaina',
    '/lainat/joustoluotto',
    '/lainat/lomalaina',
    '/lainat/haalaina',
    '/lainat/opintolaina',
    '/lainat/caravanrahoitus',
    '/lainat/ulosottolaskuri',
    '/lainat/pikalaina',
    '/lainat/yrityslaina',
    '/lainat/pienkonelaina',
    '/lainat/pienlainat-luottokortit'
  ];
  
  for (const route of allRoutes) {
    const seoData = getEnhancedSeoDataForRoute(route);
    await saveEnhancedSeoMetadata(route, seoData);
    
    // Small delay to avoid rate limiting
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  console.log('All page SEO data updated');
};

// Generate breadcrumb schema
export const generateBreadcrumbSchema = (breadcrumbs: Array<{name: string, url: string}>) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `https://lainasampo.fi${crumb.url}`
    }))
  };
};
